import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InfoPanel, Typography } from '@otcmarketsgroup/otcm-ui-lib';
import { cancelCuf, getCurrentProfile } from '../../api/cuf';
import { appStatuses, warnings } from '../../constants';
import { useSyncApplication } from '../../hooks/useSyncApplication';
import { testIds } from '../../constants/testIds';

const CancelForm = ({ symbol, email, open, onOpenChange }) => {
    const [showDialog, setShowDialog] = useState(false);
    const queryClient = useQueryClient();
    const isControlled = typeof open === 'boolean';
    const dialogOpen = isControlled ? open : showDialog;

    const setDialogState = (nextOpen) => {
        if (!isControlled) {
            setShowDialog(nextOpen);
        }
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: ['currentApplication', symbol, email] });
        };
    }, []);

    const handleCloseDialog = () => {
        if (cancelMutation.isPending) return;
        setDialogState(false);
    };

    const currentApplicationQuery = useQuery({
        queryKey: ['currentApplication', symbol, email],
        queryFn: ({ signal }) => getCurrentProfile({ symbol, email }, signal),
        retry: false,
        enabled: false,
    });

    useSyncApplication({ ...currentApplicationQuery?.data, status: { id: appStatuses.NO_APPLICATION } }, currentApplicationQuery.isSuccess);

    useEffect(() => {
        if (currentApplicationQuery.isSuccess) {
            setDialogState(false);
        }
    }, [currentApplicationQuery.isSuccess]);

    const cancelMutation = useMutation({
        mutationFn: () => cancelCuf({ symbol, email }),
        onSuccess: () => {
            currentApplicationQuery.refetch();
        },
        onError: (err) => {
            console.error(err);
        }
    });

    const isDataFetching = (cancelMutation.isPending || currentApplicationQuery.isFetching);

    return (
        <>
            <Button
                variant="outlined"
                color="warning"
                fullWidth
                data-testid={testIds.controlPanel.discardBtn}
                onClick={() => setDialogState(true)}
            >
                Discard Changes
            </Button>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} data-testid={testIds.controlPanel.discardDialog}>
                <DialogTitle title="Cancel Profile" onClose={handleCloseDialog} />
                <DialogContent>
                    {isDataFetching
                        ? <InfoPanel isWarning title={warnings.DO_NOT_REFRESH} />
                        : 'Are you sure you want to discard all changes? This action will abandon all changes currently in progress.'
                    }
                    {cancelMutation.isError && (
                        <Box mt={2}>
                            <InfoPanel isWarning title="Unable to cancel profile" />
                        </Box>
                    )}
                    {currentApplicationQuery.isError && (
                        <Box mt={2}>
                            <InfoPanel isWarning title='Unable to fetch application. Refresh page.' />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="warning"
                        data-testid={testIds.controlPanel.discardBackBtn}
                        disabled={isDataFetching}
                        onClick={handleCloseDialog}
                    >
                        No, go back
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        data-testid={testIds.controlPanel.discardConfirmBtn}
                        isLoading={isDataFetching}
                        disabled={isDataFetching}
                        onClick={() => cancelMutation.mutate()}
                    >
                        Yes, I understand
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

CancelForm.propTypes = {
    symbol: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func
};

export default CancelForm;